import React from 'react';
import { Page, Text, View, Document, StyleSheet } from '@react-pdf/renderer';

// Premium color scheme
const COLORS = {
  primary: '#1e3a8a',
  secondary: '#374151',
  accent: '#2563eb',
  success: '#059669',
  danger: '#dc2626',
  warning: '#d97706',
  light: '#f8fafc',
  dark: '#1e293b',
  border: '#e2e8f0',
  gold: '#d4af37'
};

const styles = StyleSheet.create({
  page: {
    padding: 20,
    fontFamily: 'Helvetica',
    fontSize: 9,
    backgroundColor: '#ffffff',
    lineHeight: 1.3,
  },
  container: {
    border: `1.5px solid ${COLORS.primary}`,
    borderRadius: 8,
    padding: 15,
    position: 'relative',
  },
  decorativeHeader: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 4,
    backgroundColor: COLORS.primary,
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 20,
    paddingBottom: 15,
    borderBottom: `2px solid ${COLORS.border}`,
  },
  companyInfo: {
    flex: 2,
  },
  companyName: {
    fontSize: 16,
    fontWeight: 'bold',
    color: COLORS.primary,
    marginBottom: 6,
    letterSpacing: 0.3,
  },
  companyDetails: {
    fontSize: 7.5,
    color: COLORS.secondary,
    lineHeight: 1.4,
  },
  documentInfo: {
    flex: 1,
    alignItems: 'flex-end',
  },
  documentTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    color: COLORS.accent,
    marginBottom: 6,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  documentDetails: {
    fontSize: 7.5,
    color: COLORS.secondary,
    textAlign: 'right',
    lineHeight: 1.4,
  },
  
  // Two column layout
  twoColumn: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 12,
    gap: 15,
  },
  column: {
    width: '48%',
  },
  
  // Section styles
  section: {
    marginBottom: 12,
  },
  sectionHeader: {
    backgroundColor: COLORS.primary,
    padding: 5,
    marginBottom: 6,
    borderRadius: 3,
    borderLeft: `3px solid ${COLORS.gold}`,
  },
  sectionTitle: {
    fontSize: 10,
    fontWeight: 'bold',
    color: '#ffffff',
    textTransform: 'uppercase',
    letterSpacing: 0.3,
  },
  
  // Data row styles
  dataRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 3,
    paddingVertical: 2,
    alignItems: 'center',
  },
  dataLabel: {
    fontWeight: 'normal',
    color: COLORS.dark,
    width: '60%',
    fontSize: 8,
  },
  dataValue: {
    fontWeight: 'normal',
    color: COLORS.secondary,
    width: '40%',
    textAlign: 'right',
    fontSize: 8,
  },
  highlightRow: {
    backgroundColor: COLORS.light,
    padding: 5,
    borderRadius: 4,
    marginTop: 4,
    border: `1px solid ${COLORS.border}`,
  },
  totalRow: {
    borderTop: `1.5px solid ${COLORS.border}`,
    paddingTop: 4,
    marginTop: 4,
    fontWeight: 'bold',
  },
  
  // Table styles
  table: {
    marginTop: 6,
    border: `1px solid ${COLORS.border}`,
    borderRadius: 4,
    overflow: 'hidden',
    backgroundColor: '#ffffff',
  },
  tableRow: {
    flexDirection: 'row',
    borderBottom: `1px solid ${COLORS.border}`,
    minHeight: 20,
  },
  tableHeader: {
    backgroundColor: COLORS.primary,
    fontWeight: 'bold',
  },
  tableCell: {
    padding: 4,
    borderRight: `1px solid ${COLORS.border}`,
    fontSize: 8,
  },
  tableCellFirst: {
    width: '70%',
  },
  tableCellLast: {
    width: '30%',
    textAlign: 'right',
    borderRight: 'none',
  },
  
  // Amount styling
  amount: {
    fontWeight: 'bold',
    color: COLORS.success,
    fontSize: 8,
  },
  deductionAmount: {
    fontWeight: 'bold',
    color: COLORS.danger,
    fontSize: 8,
  },
  netSalary: {
    fontSize: 10,
    fontWeight: 'bold',
    color: COLORS.primary,
  },
  
  // Footer
  footer: {
    marginTop: 20,
    paddingTop: 12,
    borderTop: `1.5px solid ${COLORS.border}`,
    textAlign: 'center',
  },
  footerText: {
    fontSize: 7,
    color: COLORS.secondary,
    marginBottom: 2,
    fontStyle: 'italic',
  },
  
  // Signature section
  signatureSection: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 25,
  },
  signatureBox: {
    width: '45%',
    textAlign: 'center',
  },
  signatureLine: {
    borderTop: `1px solid ${COLORS.dark}`,
    marginTop: 30,
    marginBottom: 6,
  },
  signatureLabel: {
    fontSize: 8,
    color: COLORS.secondary,
  },
  
  // Status badge
  statusBadge: {
    backgroundColor: COLORS.success,
    color: 'white',
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 10,
    fontSize: 7,
    fontWeight: 'bold',
  },
  
  // Compact grid for attendance
  compactGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginTop: 4,
  },
  compactItem: {
    width: '50%',
    marginBottom: 2,
  }
});


// Helper function to format date
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    return new Date(dateString + '-01').toLocaleDateString('en-IN', { 
      year: 'numeric', 
      month: 'long' 
    });
  } catch {
    return 'Invalid Date';
  }
};






const SalarySlipPDF = ({ payrollData, employeeData, companyData, holidays = []}) => {
  // Helper function to get total days in month
const getDaysInMonth = (payrollMonth) => {
  if (!payrollMonth) return 0;
  const [year, month] = payrollMonth.split('-').map(Number);
  return new Date(year, month, 0).getDate();
};

// Helper function to calculate weekly holiday count in month
const getWeeklyHolidayCount = (payrollMonth, weeklyHoliday) => {
  if (!payrollMonth || !weeklyHoliday || weeklyHoliday.length === 0) return 0;
  const [year, month] = payrollMonth.split('-').map(Number);
  const daysInMonth = new Date(year, month, 0).getDate();
  const dayNames = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
  const holidayDays = weeklyHoliday.map(day => dayNames.indexOf(day.toLowerCase())).filter(day => day !== -1);
  
  let count = 0;
  for (let day = 1; day <= daysInMonth; day++) {
    const date = new Date(year, month - 1, day);
    if (holidayDays.includes(date.getDay())) {
      count++;
    }
  }
  return count;
};

// Helper function to get company holiday count
const getCompanyHolidayCount = (payrollMonth, holidays) => {
  if (!payrollMonth || !holidays || holidays.length === 0) return 0;
  const [year, month] = payrollMonth.split('-').map(Number);
  return holidays.filter(holiday => {
    const holidayDate = new Date(holiday.date);
    return holidayDate.getFullYear() === year && holidayDate.getMonth() === month - 1;
  }).length;
};
    // Calculate and log key values (mirroring PDF component logic)
    const totalDaysInMonth = getDaysInMonth(payrollData.payrollMonth);
    const weeklyHolidayCount = getWeeklyHolidayCount(payrollData.payrollMonth, employeeData.weeklyHoliday || []);
    const companyHolidayCount = getCompanyHolidayCount(payrollData.payrollMonth, holidays);
    const totalDeductions = payrollData.deductions?.reduce((sum, ded) => sum + (ded.amount || 0), 0) || 0;
    const totalIncomes = payrollData.incomes?.reduce((sum, inc) => sum + (inc.amount || 0), 0) || 0;
    const baseSalary = payrollData.salary || 0;
    const perDaySalary = totalDaysInMonth > 0 ? baseSalary / totalDaysInMonth : 0;
    const effectiveWorkingDays = (payrollData.totalWorkingDays || 0) + 
                                ((payrollData.totalHalfDays || 0) * 0.5) + 
                                (payrollData.paidLeaves || 0) + 
                                companyHolidayCount + 
                                weeklyHolidayCount;
    const grossSalary = (perDaySalary * effectiveWorkingDays) + totalIncomes;
    const netSalary = payrollData.netSalary || 0;


  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Main Container with Decorative Header */}
        <View style={styles.container}>
          <View style={styles.decorativeHeader} />
          
          {/* Header Section */}
          <View style={styles.header}>
            <View style={styles.companyInfo}>
              <Text style={styles.companyName}>
                {companyData?.businessName || companyData?.companyName || 'COMPANY NAME'}
              </Text>
              <Text style={styles.companyDetails}>
                {companyData?.address || 'Company Address'}{'\n'}
                • Phone: {companyData?.businessPhone || 'N/A'}&nbsp;&nbsp; 
                • Email: {companyData?.businessEmail || 'N/A'}
              </Text>
            </View>
            <View style={styles.documentInfo}>
              <Text style={styles.documentTitle}>
                SALARY SLIP
              </Text>
              <Text style={styles.documentDetails}>
                For {formatDate(payrollData.payrollMonth)} 
            &nbsp;&nbsp;  Generated: {new Date().toLocaleDateString('en-IN')} 
                <Text style={styles.statusBadge}> PROCESSED </Text>
              </Text>
            </View>
          </View>

          {/* Employee and Payment Details in Two Columns */}
          <View style={styles.twoColumn}>
            {/* Employee Details */}
            <View style={styles.column}>
              <View style={styles.sectionHeader}>
                <Text style={styles.sectionTitle}>Employee Information</Text>
              </View>
              <View style={styles.dataRow}>
                <Text style={styles.dataLabel}>Employee Name:</Text>
                <Text style={styles.dataValue}>{employeeData?.teamMemberName || employeeData?.name || 'N/A'}</Text>
              </View>
              <View style={styles.dataRow}>
                <Text style={styles.dataLabel}>Department:</Text>
                <Text style={styles.dataValue}>{employeeData?.departmentName || 'N/A'}</Text>
              </View>
              <View style={styles.dataRow}>
                <Text style={styles.dataLabel}>Designation:</Text>
                <Text style={styles.dataValue}>{employeeData?.designationName || 'N/A'}</Text>
              </View>
              <View style={styles.dataRow}>
                <Text style={styles.dataLabel}>Joining Date:</Text>
                <Text style={styles.dataValue}>
                  {employeeData?.dateOfJoining ? new Date(employeeData.dateOfJoining).toLocaleDateString('en-IN') : 'N/A'}
                </Text>
              </View>
            </View>

            {/* Payment Summary */}
            <View style={styles.column}>
              <View style={styles.sectionHeader}>
                <Text style={styles.sectionTitle}>Payment Summary</Text>
              </View>
              <View style={styles.dataRow}>
                <Text style={styles.dataLabel}>Base Salary:</Text>
                <Text style={styles.dataValue}>Rs. {baseSalary.toLocaleString('en-IN')}</Text>
              </View>
              <View style={styles.dataRow}>
                <Text style={styles.dataLabel}>Additional Income:</Text>
                <Text style={styles.dataValue}>Rs. {totalIncomes.toLocaleString('en-IN')}</Text>
              </View>
              <View style={styles.dataRow}>
                <Text style={styles.dataLabel}>Salary for the Month:</Text>
                <Text style={styles.dataValue}>Rs. {grossSalary.toLocaleString('en-IN')}</Text>
              </View>
              <View style={styles.dataRow}>
                <Text style={styles.dataLabel}>Total Deductions:</Text>
                <Text style={[styles.dataValue, styles.deductionAmount]}>Rs. {totalDeductions.toLocaleString('en-IN')}</Text>
              </View>
              <View style={[styles.dataRow, styles.highlightRow, styles.totalRow]}>
                <Text style={[styles.dataLabel, styles.netSalary]}>NET SALARY:</Text>
                <Text style={[styles.dataValue, styles.amount, styles.netSalary]}>
                  Rs. {Number(netSalary || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </Text>
              </View>
            </View>
          </View>

          {/* Attendance Summary */}
          <View style={styles.section}>
            <View style={styles.sectionHeader}>
              <Text style={styles.sectionTitle}>Attendance Summary</Text>
            </View>
            <View style={styles.compactGrid}>
              <View style={styles.compactItem}>
                <View style={styles.dataRow}>
                  <Text style={styles.dataLabel}>Total Days in Month: <Text style={{ fontWeight: 'bold' }}>{totalDaysInMonth}</Text></Text>
                </View>
              </View>
              <View style={styles.compactItem}>
                <View style={styles.dataRow}>
                  <Text style={styles.dataLabel}>Working Days: <Text style={{ fontWeight: 'bold' }}>{(payrollData.totalWorkingDays || 0) + ((payrollData.totalHalfDays || 0) / 2)}</Text></Text>
                </View>
              </View>
              <View style={styles.compactItem}>
                <View style={styles.dataRow}>
                  <Text style={styles.dataLabel}>Present Days: <Text style={{ fontWeight: 'bold' }}>{payrollData.totalWorkingDays || 0}</Text></Text>
                </View>
              </View>
              <View style={styles.compactItem}>
                <View style={styles.dataRow}>
                  <Text style={styles.dataLabel}>Weekly Holidays: <Text style={{ fontWeight: 'bold' }}>{weeklyHolidayCount}</Text></Text>
                </View>
              </View>
              <View style={styles.compactItem}>
                <View style={styles.dataRow}>
                  <Text style={styles.dataLabel}>Paid Leaves: <Text style={{ fontWeight: 'bold' }}>{payrollData.paidLeaves || 0}</Text></Text>
                </View>
              </View>
              <View style={styles.compactItem}>
                <View style={styles.dataRow}>
                  <Text style={styles.dataLabel}>Half Days: <Text style={{ fontWeight: 'bold' }}>{payrollData.totalHalfDays || 0}</Text></Text>
                </View>
              </View>
              <View style={styles.compactItem}>
                <View style={styles.dataRow}>
                  <Text style={styles.dataLabel}>Company Holidays: <Text style={{ fontWeight: 'bold' }}>{companyHolidayCount}</Text></Text>
                </View>
              </View>
            </View>
          </View>

          {/* Deductions Table */}
          {payrollData.deductions && payrollData.deductions.length > 0 && (
            <View style={styles.section}>
              <View style={styles.sectionHeader}>
                <Text style={styles.sectionTitle}>Deductions Breakdown</Text>
              </View>
              <View style={styles.table}>
                <View style={[styles.tableRow, styles.tableHeader]}>
                  <Text style={[styles.tableCell, styles.tableCellFirst]}>Deduction Type</Text>
                  <Text style={[styles.tableCell, styles.tableCellLast]}>Amount</Text>
                </View>
                {payrollData.deductions.map((deduction, index) => (
                  <View key={index} style={styles.tableRow}>
                    <Text style={[styles.tableCell, styles.tableCellFirst]}>{deduction.name || deduction.type || 'N/A'}</Text>
                    <Text style={[styles.tableCell, styles.tableCellLast, styles.deductionAmount]}>
                      Rs. {(deduction.amount || 0).toLocaleString('en-IN')}
                    </Text>
                  </View>
                ))}
                <View style={[styles.tableRow, styles.totalRow]}>
                  <Text style={[styles.tableCell, styles.tableCellFirst, {fontWeight: 'bold'}]}>Total Deductions</Text>
                  <Text style={[styles.tableCell, styles.tableCellLast, styles.deductionAmount, {fontWeight: 'bold'}]}>
                    Rs. {totalDeductions.toLocaleString('en-IN')}
                  </Text>
                </View>
              </View>
            </View>
          )}

          {/* Additional Income Table */}
          {payrollData.incomes && payrollData.incomes.length > 0 && (
            <View style={styles.section}>
              <View style={styles.sectionHeader}>
                <Text style={styles.sectionTitle}>Additional Income</Text>
              </View>
              <View style={styles.table}>
                <View style={[styles.tableRow, styles.tableHeader]}>
                  <Text style={[styles.tableCell, styles.tableCellFirst]}>Income Type</Text>
                  <Text style={[styles.tableCell, styles.tableCellLast]}>Amount</Text>
                </View>
                {payrollData.incomes.map((income, index) => (
                  <View key={index} style={styles.tableRow}>
                    <Text style={[styles.tableCell, styles.tableCellFirst]}>{income.name || income.type || 'N/A'}</Text>
                    <Text style={[styles.tableCell, styles.tableCellLast, styles.amount]}>
                      Rs. {(income.amount || 0).toLocaleString('en-IN')}
                    </Text>
                  </View>
                                  ))}
                <View style={[styles.tableRow, styles.totalRow]}>
                  <Text style={[styles.tableCell, styles.tableCellFirst, {fontWeight: 'bold'}]}>Total Additional Income</Text>
                  <Text style={[styles.tableCell, styles.tableCellLast, styles.amount, {fontWeight: 'bold'}]}>
                    Rs. {totalIncomes.toLocaleString('en-IN')}
                  </Text>
                </View>
              </View>
            </View>
          )}

          {/* Footer Notes */}
          <View style={styles.footer}>
            <Text style={styles.footerText}>
              This is a system generated salary slip and does not require signature.
            </Text>
            <Text style={styles.footerText}>
              For any discrepancies, please contact the HR department within 7 days of receipt.
            </Text>
          </View>

          {/* Signatures */}
          <View style={styles.signatureSection}>
            <View style={styles.signatureBox}>
              <View style={styles.signatureLine} />
              <Text style={styles.signatureLabel}>Employee Signature</Text>
            </View>
            <View style={styles.signatureBox}>
              <View style={styles.signatureLine} />
              <Text style={styles.signatureLabel}>Authorized Signatory</Text>
              <Text style={[styles.signatureLabel, {fontSize: 7, marginTop: 2}]}>
                {companyData?.businessName || companyData?.companyName || 'Company Name'}
              </Text>
            </View>
          </View>
        </View>
      </Page>
    </Document>
  );
};

export default SalarySlipPDF;
